import React, { useEffect, createRef, useState } from 'react';
import { Snippet } from 'react-instantsearch/dom';

import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import ClampedText from 'components/shared/Text';
import { routes } from 'constants/config';
import LESSON_STATES from 'constants/lesson_states';
import { isIE } from 'utils/browserCheck';
import AdvancedSearchResultCardBack from './AdvancedSearchResultCardBack';
import ContentTypeAndStatus from './ContentTypeAndStatus';

const getUrlsForContent = content => {
  switch (content.content_type) {
    case 'path':
      return {
        overview: routes.PATH_VIEW(content.p_id),
        preview: routes.PATH_PREVIEW(content.p_id),
        edit: routes.PATH_EDIT(content.p_id),
      };
    case 'practice_container':
      return {
        overview: routes.ON_DEMAND_PRACTICE.CREATOR.SHOW(content.ps_id),
        preview: routes.ON_DEMAND_PRACTICE.PREVIEW.SHOW(content.ps_id),
        edit: routes.ON_DEMAND_PRACTICE.CREATOR.EDIT(content.ps_id),
      };
    case 'draft_lesson':
      return {
        overview: routes.LESSON(content.parent_lesson_id),
        preview: routes.LESSON_OVERVIEW.PREVIEW_LESSON_DRAFT(
          content.parent_lesson_id,
        ),
        edit: routes.LESSON_OVERVIEW.EDIT_LESSON(content.parent_lesson_id),
      };
    default:
      return {
        overview: routes.LESSON(content.l_id),
        preview: routes.LESSON_OVERVIEW.PREVIEW_LESSON(content.l_id),
        edit: routes.LESSON_OVERVIEW.EDIT_LESSON(content.l_id),
      };
  }
};

// Due to accessibilty issues with IE11 and the hover design for the cards
// we need to discern between IE and the rest of the broswers to keep full functionality to IE
// but without the accessibility focus ring via keyboard.
export const CardComponent = props => {
  if (isIE()) {
    return <div {...props} />;
  }
  return <button type="button" {...props} />;
};

export default function AdvancedSearchResultCard({ content, editable }) {
  const {
    content_type: contentType,
    state,
    is_scorm: isScorm,
    certification_id: certificationId,
  } = content;
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const urls = getUrlsForContent(content);

  const cardRef = createRef();
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused && !cardRef.current?.contains(document.activeElement)) {
      setIsCardFlipped(false);
    }
  }, [isFocused, cardRef, setIsCardFlipped]);

  return (
    <CardComponent
      onClick={() => {
        setIsCardFlipped(!isCardFlipped);
        setIsFocused(!isFocused);
      }}
      onBlur={() => setIsFocused(false)}
      onMouseEnter={() => {
        setIsCardFlipped(true);
        setIsFocused(true);
      }}
      onMouseLeave={() => {
        setIsCardFlipped(false);
        setIsFocused(false);
      }}
      className="AdvancedSearchResultCard u-resetBtn bg-white d-f ta-l fd-c wb-bw"
    >
      {isCardFlipped && (
        <AdvancedSearchResultCardBack
          urls={urls}
          isScorm={isScorm}
          editable={editable}
          isDraft={
            state === LESSON_STATES.DRAFT ||
            state === LESSON_STATES.NEW_VERSION_PENDING
          }
          certificationId={certificationId}
          contentType={contentType}
          setIsCardFlipped={setIsCardFlipped}
          ref={cardRef}
          setIsFocused={setIsFocused}
        />
      )}
      <Text
        lines={2}
        component={ClampedText}
        className="mw-100 ov-h"
        variant={TEXT_VARIANTS.BODY_BOLD}
      >
        <Snippet hit={content} attribute="title" />
      </Text>
      <Text
        className="mt-24 ov-h"
        lines={5}
        component={ClampedText}
        variant={TEXT_VARIANTS.BODY}
      >
        <Snippet hit={content} attribute="description" />
      </Text>
      <ContentTypeAndStatus
        state={state}
        isScorm={isScorm}
        contentType={contentType}
        certificationId={certificationId}
      />
    </CardComponent>
  );
}
